<template>
  <v-container class="pt-0" id="PaymentPrintable">
    <v-row style="margin-bottom: 5px">
      <v-col cols="12" style="margin-top: 20px; margin-bottom: 80px" class="showOnlyForPrint">
        <img src="https://www.primerica.com/public/images/primerica-logo.png" alt="PrimericaLogo" />
      </v-col>
    </v-row>

    <v-row style="margin-bottom: 80px; font-size: 16px; font-weight: bold" class="showOnlyForPrint">
      <v-col style="display: inline">
        {{ $t('L_payment_authorization') }}
      </v-col>
      <v-col style="display: inline">
        {{ todayDate }}
      </v-col>
    </v-row>

    <!-- Your payment authorization has been received. -->
    <v-row style="margin-bottom: 20px" class="addSpaceBelow">
      <v-col style="font-size: 16px; font-weight: bold; display: inline">
        <span>{{ $t('L_your_payment_authorization_has_been_received') }}</span>
      </v-col>
    </v-row>

    <!-- We've received your payment authorization for your life policy -->
    <v-row style="margin-bottom: 20px" class="addSpaceBelow">
      <v-col cols="12" style="font-size: 16px">
        {{
          $t('L_we_have_received_your_payment_authorization_for_your_life', {
            policyNum: getPolicyNumber,
          })
        }}
      </v-col>
    </v-row>

    <!-- Your payment is being processed. -->
    <v-row style="margin-bottom: 20px">
      <v-col cols="12" style="font-size: 14px">
        <span style="font-weight: regular">{{ $t('L_your_payment_is_being_processed') }}</span>
      </v-col>
    </v-row>

    <!-- Here are the details of your authorization -->
    <v-row style="margin-bottom: 8px">
      <v-col cols="12" style="font-size: 16px; font-weight: bold">
        <span>{{ $t('L_here_are_the_details_of_your_authorizations') }}</span>
      </v-col>
    </v-row>

    <v-divider class="customDividier addSpaceBelow" />

    <v-row style="margin-bottom: 20px; margin-top: 10px">
      <v-col cols="12" class="addHalfSpaceBelow">
        <span style="font-size: 12px">{{ $t('L_payment_amount') }}</span>
      </v-col>
      <v-col cols="12" style="font-size: 16px; font-weight: bold">$ {{ getPaymentAmount }}</v-col>
    </v-row>

    <v-row style="margin-bottom: 10px">
      <v-col style="font-size: 12px" class="addHalfSpaceBelow">
        {{ $t('L_bank_account') }}
      </v-col>
      <v-col cols="12" style="font-size: 16px; font-weight: bold">
        {{ getPaymentMethod }}
      </v-col>
      <v-col cols="12" style="font-size: 14px; font-weight: bold">
        {{ getBankName }}
      </v-col>
    </v-row>

    <v-divider class="customDividier addSpaceBelow" />

    <v-row style="margin-bottom: 20px; margin-top: 10px">
      <v-col cols="12" style="font-size: 16px">
        <span>{{ $t('L_an_email_confirmation_has_also_been_sent_to', { email: userEmail }) }}</span>
      </v-col>
    </v-row>

    <v-row class="hideForPrint" v-if="!isMobile">
      <v-col
        @click="printDoc"
        cols="12"
        class="mb-4 text-left font-weight-bold font-size--18 blueText"
      >
        <v-icon color="#0c6dca" :icon="mdiPrinter" />
        {{ $t('L_print_payment_confirmation') }}
      </v-col>
    </v-row>
    <v-row class="d-flex justify-end mt-4 pt-4 mb-4 pb-4 hideForPrint">
      <v-col cols="12" sm="4" md="3" class="px-4">
        <OnexButton block @click="done">
          {{ $t('L_done') }}
        </OnexButton>
      </v-col>
    </v-row>
    <!-- For Print Only -->
    <v-row style="position: relative; text-align: center" class="showOnlyForPrint">
      <v-col cols="12">
        <p
          style="
            position: fixed;
            bottom: 0;
            width: 100%;
            padding-bottom: env(safe-area-inset-bottom);
          "
        >
          &#169; {{ getCurrentYear }} Primerica www.myprimerica.com
        </p>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { mapActions, mapState } from 'pinia';
import { useGlobalStore } from '@/stores/global/global.store';
import { format } from 'date-fns';
import { Translations } from '@/i18n/models';
import { mdiPrinter } from '@mdi/js';
import { OnexButton } from '@primerica/onex-ui-lib';

export const giveDate = (date: string, isCanada: boolean) => {
  if (date.length !== 6) {
    return;
  }
  const month = date.slice(0, 2);
  const day = date.slice(2, 4);
  const year = `20${date.slice(4, 6)}`;

  return isCanada ? `${year}/${month}/${day}` : `${month}/${day}/${year}`;
};

const componentOptions: ComponentOptions = {
  name: 'PayStepFour',
  components: {
    OnexButton,
  },
  data: () => ({ Translations, mdiPrinter }),
  computed: {
    ...mapState(useCustomerStore, ['customerProfile']),
    ...mapState(usePolicyStore, [
      'policies',
      'currentPolicy',
      'routeNameToNavigateBack',
      'selectedPolicyIndex',
      'paymentData',
    ]),
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
    }),
    todayDate() {
      return format(new Date(), 'MMMM dd, yyyy');
    },
    getCurrentYear() {
      return format(new Date(), 'yyyy');
    },
    nextDraftDate() {
      return this.currentPolicy?.paymentInfo?.nextCycleDate
        ? giveDate(this.currentPolicy?.paymentInfo?.nextCycleDate, false)
        : '';
    },
    getPolicyNumber() {
      if (this.currentPolicy) {
        return this.currentPolicy.contractId;
      }
      return '';
    },
    getPaymentAmount() {
      if (this.paymentData?.amount) {
        return `${this.paymentData.amount}`;
      }
      return '';
    },
    getPaymentMethod() {
      if (this.paymentData?.accountNumber) {
        return `****${this.paymentData?.accountNumber?.slice(-4)} ${this.paymentData.accountType}`;
      }
      return '';
    },
    getBankName() {
      if (this.paymentData?.bankName) {
        return this.paymentData.bankName;
      }
      return '';
    },
    isCanadaPolicy() {
      return !!this.currentPolicy?.isCanadaPolicy;
    },
    userEmail() {
      return this.customerProfile.email || this?.currentPolicy?.owner?.email;
    },
    isMobile() {
      const userAgent = navigator.userAgent;
      const retVal =
        userAgent.includes('Android') ||
        userAgent.includes('webOS') ||
        userAgent.includes('iPhone') ||
        userAgent.includes('iPad') ||
        userAgent.includes('iPod') ||
        userAgent.includes('IEMobile') ||
        userAgent.includes('Opera Mini') ||
        userAgent.includes('BlackBerry');
      return retVal;
    },
  },
  methods: {
    ...mapActions(useGlobalStore, {
      removeAlerts: 'removeAlerts',
    }),
    printDoc() {
      // * Dynatrace Tag (https://primerica.atlassian.net/wiki/spaces/PD/pages/19955873/Payments+-+Custom+Dynatrace+Tags)
      try {
        const i = this.$dynatrace.enterAction('OneTimePayment_PrintConfirmation_PageDisplayed');
        this.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
      const contents = document.getElementById('PaymentPrintable')?.innerHTML;
      const frame1 = document.createElement('iframe');
      frame1.name = 'frame1';
      frame1.style.position = 'absolute';
      frame1.style.top = '-1000000px';
      document.body.appendChild(frame1);

      const frameDoc = frame1.contentWindow
        ? frame1.contentWindow.document
        : frame1.contentDocument;
      frameDoc?.open();
      if (this.locale === 'fr_CA') {
        frameDoc?.write('<html lang="fr"><head><title>MyPrimerica</title>');
      } else {
        frameDoc?.write('<html lang="en"><head><title>MyPrimerica</title>');
      }
      // frameDoc?.write('<link rel="stylesheet" type="text/css" href="/css/print.css"/>');
      frameDoc?.write('<style>');
      frameDoc?.write('.hideForPrint {');
      frameDoc?.write('  display: none;');
      frameDoc?.write('}');
      frameDoc?.write('.showOnlyForPrint {');
      frameDoc?.write('display: block;');
      frameDoc?.write('}');
      frameDoc?.write('.hideinlineForPrint {');
      frameDoc?.write('display: none;');
      frameDoc?.write('}');
      frameDoc?.write('.addSpaceBelow {');
      frameDoc?.write('margin-bottom: 25px;');
      frameDoc?.write('}');
      frameDoc?.write('.addHalfSpaceBelow {');
      frameDoc?.write('margin-bottom: 15px;');
      frameDoc?.write('}');
      frameDoc?.write('</style>');
      frameDoc?.write('</head><body>');
      frameDoc?.write(contents ?? '');
      frameDoc?.write('</body></html>');
      frameDoc?.close();
      setTimeout(() => {
        frame1.focus();
        frame1.contentWindow?.print();
        document.body.removeChild(frame1);
      }, 500);
      return false;
    },
    done() {
      // * Dynatrace Tag (https://primerica.atlassian.net/wiki/spaces/PD/pages/19955873/Payments+-+Custom+Dynatrace+Tags)
      try {
        const i = this.$dynatrace.enterAction('OneTimePayment_Done_Selected');
        this.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
      this.$emit('goToStepFive');
    },
  },
  mounted() {},
};

export default defineComponent(componentOptions);
</script>

<style>
.bg-light-grey {
  background-color: #f5f5f5;
}
.blueText {
  color: #0c6dca;
}
.marginbottomline {
  margin-bottom: 15px;
}
.marginbottomhalfline {
  margin-bottom: 8px;
}
.customDividier {
  border-top: 4px solid rgba(6, 53, 98, 0.3);
}
.showOnlyForPrint {
  display: none;
}
.hideinlineForPrint {
  display: inline;
}
.buttonCSS {
  float: right;
  max-width: 300px;
  width: 300px;
  margin: auto;
}
.buttonCSSMobile {
  float: left;
  max-width: 226px;
  width: 226px;
}
</style>
